/*
 * Helpers for the OneTrust Web CMP. Opening prefers the documented
 * `ToggleInfoDisplay()`; the hidden floating button is only a fallback.
 */

/* Deep link used by the published cookie policy pages. */
export const COOKIE_SETTINGS_HASH = '#cookie-setting';

export function isCookieSettingsHash (hash?: string | null): boolean {
  return (hash || '').toLowerCase() === COOKIE_SETTINGS_HASH;
}

/* Returns false when OneTrust is not available yet, so the caller can defer. */
export function openPreferenceCenter (): boolean {
  if (typeof window === 'undefined') { return false; }

  if (typeof window.OneTrust?.ToggleInfoDisplay === 'function') {
    window.OneTrust.ToggleInfoDisplay();
    return true;
  }

  const floatingButton = document.querySelector<HTMLElement>('.ot-floating-button__open');
  if (floatingButton) {
    floatingButton.click();
    return true;
  }

  return false;
}

/* OneTrust's Preference Center dialog — internal but stable markup. */
const PREFERENCE_CENTER_ID = 'onetrust-pc-sdk';

/*
 * Open = the dialog exists and none of OneTrust's hiding mechanisms —
 * inline display, `aria-hidden`, `ot-hide` — is applied. Checked lazily.
 */
export function isPreferenceCenterOpen (): boolean {
  if (typeof window === 'undefined') { return false; }

  const dialog = document.getElementById(PREFERENCE_CENTER_ID);
  if (!dialog) { return false; }

  return dialog.style.display !== 'none' &&
    dialog.getAttribute('aria-hidden') !== 'true' &&
    !dialog.classList.contains('ot-hide');
}

/*
 * `Close()` is documented for the banner but is the only API that also
 * dismisses the Preference Center (`ToggleInfoDisplay()` won't, no X exists).
 */
export function closePreferenceCenter (): boolean {
  if (typeof window === 'undefined') { return false; }

  if (typeof window.OneTrust?.Close === 'function') {
    window.OneTrust.Close();
    return true;
  }

  return false;
}

/*
 * `IsAlertBoxClosed` = a still-valid consent decision exists; false before
 * any interaction or when a re-prompt is due. `null` = SDK not loaded yet —
 * callers must defer, not read it as refusal.
 */
export function hasClosedConsentBanner (): boolean | null {
  if (typeof window === 'undefined') { return null; }

  if (typeof window.OneTrust?.IsAlertBoxClosed !== 'function') { return null; }

  return window.OneTrust.IsAlertBoxClosed();
}
